import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { Button, Spinner, Title, ErrorState } from "@common/index";
import { ComponentColor } from "@common/types";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { useShoppingCartApi } from "@/hooks/apiHooks/useShoppingCartApi";
import { useShipToApi } from "@/hooks/apiHooks/useShipToApi";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useMountRead } from "@/hooks/useMountRead";
import { categoriesAtom } from "@/store/categoryAtoms";
import { isProcessingShoppingCartAtom } from "@/store/shoppingCartAtoms";
import { buildCategoryChain } from "@utils/categoryTree";
import { asApiFailure } from "@utils/apiFailure";
import { isSameGuid } from "@utils/sameGuid";
import {
    formatPiecePricingFromCount,
    OUT_OF_STOCK,
} from "@utils/formatter";
import { vendorRowsForSku } from "@utils/vendorRows";
import { SubcategoryBreadcrumb } from "@customer/Browse/components/SubcategoryBreadcrumb";
import { ProductCarouselRow } from "@customer/Browse/components/ProductCarouselRow";
import type {
    GetAllProductsResponse,
    Product,
    ProductVariant,
    ProductVendor,
} from "@models/product";
import { ProductGallery } from "./ProductGallery";
import { QuantityStepper } from "./QuantityStepper";
import { VariantChips } from "./VariantChips";
import { InfoAccordion } from "./InfoAccordion";
import { CoaModal } from "./_components/CoaModal";
import { ProductCoaDownload } from "./_components/ProductCoaDownload";
import { ProductNotAvailable } from "./_components/ProductNotAvailable";
import { OtherVendors } from "./_components/OtherVendors";
import { isSellable, selectVendorSku } from "./_helpers/selectVendorSku";
import { otherOfferings } from "./_helpers/otherOfferings";
import { isVariantAvailable } from "./_helpers/isVariantAvailable";
import {
    NO_VENDOR_CHOICE,
    choiceAfterRead,
    type VendorChoiceState,
} from "./_helpers/vendorChoice";
import {
    addableMessage,
    cartRefusal,
    stockLimitMessage,
} from "../_helpers/stockCopy";
import { quantityCap } from "../_helpers/quantityCap";

const RELATED_LIMIT = 6;
const SHORT_DESCRIPTION_WORDS = 30;

const truncateWords = (text: string, wordCount: number): string => {
    const trimmed = text.trim();
    if (!trimmed) return "";
    const words = trimmed.split(/\s+/);
    if (words.length <= wordCount) return trimmed;
    return words.slice(0, wordCount).join(" ") + "…";
};

export const ProductDetail = () => {
    const { productId } = useParams<{ productId: string }>();
    const { getProduct } = useProductApi();
    const { addToCart } = useShoppingCartApi();
    const navigate = useNavigate();
    const { callShipToApi } = useShipToApi();
    const { getCategories } = useCategoryApi();
    const allCategories = useAtomValue(categoriesAtom);
    const isCartMutating = useAtomValue(isProcessingShoppingCartAtom);
    // The "Out of stock" line's id: the order controls it disables point at it.
    const outOfStockId = useId();

    // The last product a read loaded. The route keeps this page mounted when
    // `:productId` changes (the "More in …" rail links to products on the same
    // route), so it can be the PREVIOUS product's. Only the route's own product
    // is ever shown (#407): while B loads, A's page, whose Add to Cart buys A,
    // is not on screen, and when B's read fails, B's failure is.
    const [loadedProduct, setProduct] = useState<Product | null>(null);
    const product =
        loadedProduct !== null &&
        productId !== undefined &&
        isSameGuid(loadedProduct.id, productId)
            ? loadedProduct
            : null;
    const [quantity, setQuantity] = useState(1);
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
        null,
    );
    // The vendor the shopper chose under "Purchase from another vendor" (B-09), and the
    // one a re-read took away. Cleared on every variant change and every product read
    // (a new product, or a new ship-to destination) — see choiceAfterRead.
    const [vendorChoice, setVendorChoice] =
        useState<VendorChoiceState>(NO_VENDOR_CHOICE);
    // Choosing a vendor that has less than the quantity lowers the quantity to what it
    // has (review finding 8). Kept with the offering and the quantity it set, and shown
    // only while both are still on screen, as addRefusal is.
    const [quantityLowered, setQuantityLowered] = useState<{
        vendorSkuId: string;
        quantity: number;
        message: string;
    } | null>(null);
    // THIS page's add in flight — its button's spinner and "Adding…". Not the
    // shared cart-processing flag, which also rises for cart writes that are not
    // this add (that flag still disables the button).
    const [isAdding, setIsAdding] = useState(false);
    // The rail belongs to the product read it was fetched for (PR 373 review
    // 3), kept with that read's product object and shown only beside it. A move
    // to another product, or the same product read for a new destination, is
    // another object: its rail shows once its own rail read answers, and not at
    // all when that read fails — never the previous one's.
    const [rail, setRail] = useState<{
        forProduct: Product;
        items: Product[];
    } | null>(null);
    const [isCoaModalOpen, setIsCoaModalOpen] = useState(false);
    // The add-to-cart stock refusal (#443): the offering couldn't take the line. Kept with the
    // offering and quantity it refused and shown only while both are still on screen —
    // a different choice may well be accepted, so it must not stay beside it.
    const [addRefusal, setAddRefusal] = useState<{
        vendorSkuId: string;
        quantity: number;
        message: string;
    } | null>(null);
    // WHICH product 404'd, set by the latest read when it fails. Read only
    // together with that read's status (the not-found gate below), so it never
    // needs clearing when a new read starts: the status is already "loading"
    // on the render where the route or the destination changes.
    //
    // Kept apart from the read's status because the three outcomes of this read
    // must stay three: not-found, broken, and 200-with-a-restriction. A
    // restricted product is NOT a missing one — the API answers 200 for it
    // deliberately — so nothing about it may route through a not-found path
    // (#228 / E4-S12).
    const [notFoundProductId, setNotFoundProductId] = useState<string | null>(
        null,
    );

    // Only the LATEST read may apply what it loaded. getProduct follows the
    // ship-to destination, so a location change re-reads the SAME id, and the
    // previous destination's answer landing last must not put its restriction
    // flags and vendors back. useMountRead hands the read `isCurrent`, which
    // turns false once the hook issues a newer read.
    const readProduct = useCallback(async (isCurrent: () => boolean) => {
        if (!productId) return;
        try {
            // The page says "Product not found" itself, so only a 404 skips
            // useApi's toast; every other failure keeps it beside the Retry.
            const data = await getProduct(productId, {
                skipErrorNotification: (status) => status === 404,
            });
            // A 200 whose body is no product (callApi resolves `null`, or "" for
            // a non-JSON body). Not "not found": nothing said so. A read that
            // failed, so the retryable ErrorState.
            if (typeof data !== "object" || data === null) {
                throw new Error("The product read returned no product.");
            }
            if (!isCurrent()) return;
            setProduct(data);
            setQuantity(1);
            setVendorChoice((prev) => choiceAfterRead(prev, data));
            // BR-04: the first variant that can be bought — never one whose
            // chip is disabled. None: nothing is selected (§3.1, below).
            setSelectedVariantId(
                data.variants?.find(isVariantAvailable)?.id ?? null,
            );
        } catch (error: unknown) {
            // Duck-typed, not `instanceof ApiError`: component tests mock the
            // whole useApi module away, so the class is undefined there. Same
            // reason asApiFailure exists — see its comment.
            // Assigned on both branches: a 500 following a 404 for the same id
            // has to reach the retryable ErrorState, and the not-found cell is
            // checked first, so leaving the old id in place would hide it.
            //
            // The product on screen goes too (PR 373 review 2). A failed re-read
            // of the same id is a ship-to change whose destination could not be
            // read: the previous destination's prices, vendors and restriction
            // flags are not this one's, so the failure shows, not a page that
            // looks buyable, and its Retry is the spinner.
            if (isCurrent()) {
                const status = asApiFailure(error)?.status;
                setNotFoundProductId(status === 404 ? productId : null);
                setProduct(null);
            }
            throw error;
        }
    }, [productId, getProduct]);

    // The status of the read THIS mount issued for THIS product (#407), never
    // an app-scoped error atom: one outlives the page and is cleared only when
    // the next read starts, after the first paint.
    const { readStatus, reload } = useMountRead(readProduct);

    useEffect(() => {
        if (allCategories.length > 0) return;
        getCategories().catch(() => {
            /* notification layer surfaces errors */
        });
    }, [allCategories.length, getCategories]);

    useEffect(() => {
        if (!product) return;
        let cancelled = false;
        callShipToApi<GetAllProductsResponse>(
            `/products?pageNumber=1&pageSize=${RELATED_LIMIT + 1}&categoryIds=${product.categoryId}`,
        )
            .then((response) => {
                if (cancelled) return;
                setRail({
                    forProduct: product,
                    items: response.items
                        .filter((p) => p.id !== product.id)
                        .slice(0, RELATED_LIMIT),
                });
            })
            .catch(() => {
                /* notification layer surfaces errors */
            });
        return () => {
            cancelled = true;
        };
    }, [product, callShipToApi]);
    const relatedProducts =
        rail !== null && rail.forProduct === product ? rail.items : [];

    const categoryChain = useMemo(
        () =>
            product
                ? buildCategoryChain(allCategories, product.categoryId)
                : [],
        [allCategories, product],
    );

    const selectedVariant: ProductVariant | undefined = useMemo(() => {
        if (!product || !selectedVariantId) return undefined;
        return product.variants.find((v) => v.id === selectedVariantId);
    }, [product, selectedVariantId]);

    // The supply rows of the sku actually being bought — the selected variant's
    // array or the base sku's, narrowed to the rows tagged with THAT sku (#390)
    // — and the one row that would back its cart line. Derived ONCE and read by
    // Add to Cart, the button's disabled state and the Vendor row, so the page
    // cannot name one vendor while the button buys from another (#186), and the
    // Vendor row's "any rows at all?" check sees the same narrowed array the
    // winner is picked from. The array, the id and the cart payload's variantId
    // all come from `selectedVariant` rather than from `selectedVariantId`, so a
    // selected-but-missing variant cannot pair the base sku's rows with a
    // variant's id.
    //
    // §3.1 (BR-04): a product with variants has no buyable base sku, so with no
    // variant selected — none is available — there is nothing to buy, whatever
    // rows a base sku written beside the variants before the API stopped
    // writing one may carry.
    const hasNoSkuToBuy =
        !!product && product.variants.length > 0 && !selectedVariant;
    const selectedVendors = useMemo(
        () =>
            hasNoSkuToBuy
                ? []
                : vendorRowsForSku(
                    selectedVariant ? selectedVariant.vendors : product?.vendors,
                    selectedVariant?.id ?? null,
                ),
        [product, selectedVariant, hasNoSkuToBuy],
    );
    // The shopper's choice from "Purchase from another vendor" (B-09), while it
    // is a sellable row of this sku.
    const chosen = vendorChoice.chosen;
    const chosenVendorSku = useMemo(
        () =>
            chosen === null
                ? undefined
                : selectedVendors.find(
                    (vendor) =>
                        vendor.id === chosen.vendorSkuId && isSellable(vendor),
                ),
        [selectedVendors, chosen],
    );
    // Already narrowed above; the helper narrows again (a no-op here) because
    // its parameter is required so that no caller can opt out.
    const selectedVendorSku = useMemo(
        () =>
            chosenVendorSku ??
            selectVendorSku(selectedVendors, selectedVariant?.id ?? null),
        [chosenVendorSku, selectedVendors, selectedVariant],
    );
    const offerings = useMemo(
        () => otherOfferings(selectedVendors, selectedVendorSku?.id),
        [selectedVendors, selectedVendorSku],
    );

    // Piece fields must come from the SAME source as the displayed price —
    // never pair a variant's price with the product's piece math. The piece price
    // is derived from that price, as the cart line's is (PR 356 review).
    const priceSource = selectedVariant ?? product;
    // One vendor, one price (review finding 3): the page quotes the offering
    // the line is bought from — the cheapest by default, or the shopper's
    // choice — at the price that line is charged (§3.11). With no offering to
    // buy from, the sku's own price stands.
    const unitPrice =
        selectedVendorSku?.price ?? selectedVariant?.price ?? product?.price ?? 0;
    const pricing = formatPiecePricingFromCount(
        unitPrice,
        priceSource?.piecesPerUnit,
    );
    // PO ruling 2026-11-02: no live vendor offer for the product (or the
    // selected variant), so it can't be ordered and says "Out of stock", the
    // same as one with no stock. Keyed on the flag the ruling names, not only
    // on the availability that normally travels with it. A restricted product
    // never gets here: it has its own view above the page.
    const hasNoOffers = priceSource?.hasOffers === false;
    // isAvailable is derived server-side (any active vendor sku with
    // quantity > 0). Absent on pre-Bug-#302 payloads — treat as unavailable.
    const inStock =
        !hasNoOffers &&
        (selectedVariant
            ? isVariantAvailable(selectedVariant)
            : !!product && product.isActive && product.isAvailable === true);
    // Purchasable iff the server's availability flag AND the vendor rows agree
    // (#409): `isAvailable` is derived from the same row flags, but the rows
    // are what the cart line is backed by, so a payload where the two disagree
    // (or where the selected sku's rows are all unsellable) must disable the
    // button rather than let a click fall through the guard below in silence.
    // Disabled while ANY cart mutation is in flight, and the click is refused
    // on the same condition: a double-click on a slow connection would
    // otherwise post the line twice.
    // #443 / C-07: a line may not exceed the vendor's stock. The stepper stops at
    // it (and at the API's per-line limit), and AT it the count says why the +
    // stopped. A quantity ABOVE it — reachable only by switching to a variant
    // whose vendor has less (choosing another vendor lowers the quantity
    // instead, see handleChooseVendor) — is blocked here, with the count as the
    // reason, rather than left to the API's 400. Under the stock the count
    // limits nothing and is not shown: it is not a low-stock hint. Unknown
    // stock (an older payload) caps at the per-line limit only.
    const stockLimit = selectedVendorSku?.availableQuantity;
    const stockNote =
        stockLimit !== undefined && quantity >= stockLimit
            ? stockLimitMessage(stockLimit)
            : undefined;
    const isAboveStock = stockLimit !== undefined && quantity > stockLimit;
    // The sku can be bought at all — what "Out of stock" answers. A cart write
    // in flight only holds the button; it says nothing about the shelf.
    const isPurchasable = inStock && selectedVendorSku !== undefined;
    // Nothing to buy and nothing to price it by — no live vendor offer (P1:
    // its 0 is "no price"), or a product whose variants were all removed,
    // priced 0 (§3.1, BR-04). "$0.00" would read as free, so no price is
    // quoted; "Out of stock" below says why. An out-of-stock sku with a real
    // price keeps it.
    const isUnpriced = hasNoOffers || (!isPurchasable && unitPrice === 0);
    // The order controls point at the "Out of stock" line, which is on screen
    // exactly while the sku can't be bought.
    const outOfStockReasonId = !isPurchasable ? outOfStockId : undefined;
    // And only once the read for the CURRENT destination has loaded (PR 373
    // round 1b): a ship-to change re-reads the product with the previous
    // destination's page left up, and that page's prices, vendors and
    // restriction flags do not answer for the new one.
    const canAddToCart =
        isPurchasable &&
        !isAboveStock &&
        !isCartMutating &&
        readStatus === "loaded";
    const showQuantityLowered =
        quantityLowered !== null &&
        quantityLowered.vendorSkuId === selectedVendorSku?.id &&
        quantityLowered.quantity === quantity;

    // A variant change clears the vendor choice (review finding 4): it was a
    // choice among THAT sku's offerings, and must not return with it.
    const handleSelectVariant = useCallback((variantId: string) => {
        setSelectedVariantId(variantId);
        setVendorChoice(NO_VENDOR_CHOICE);
    }, []);

    // Makes the offering the one Add to Cart buys from. A quantity above what
    // it can take (its stock, never past the per-line limit) is lowered to
    // that, and the page says so (review finding 8), rather than leaving the
    // shopper a disabled button.
    const handleChooseVendor = useCallback(
        (offering: ProductVendor) => {
            if (!product) return;
            setVendorChoice({
                chosen: {
                    productId: product.id,
                    vendorSkuId: offering.id,
                    vendorName: offering.vendorName,
                },
                goneVendorName: null,
            });
            const cap = quantityCap(offering.availableQuantity);
            if (quantity > cap) {
                setQuantity(cap);
                setQuantityLowered({
                    vendorSkuId: offering.id,
                    quantity: cap,
                    message: `Quantity lowered to ${cap}, all ${offering.vendorName} has.`,
                });
            }
        },
        [product, quantity],
    );

    const handleAddToCart = async () => {
        if (!product) return;
        if (isCartMutating) return;
        // §3.11 shelf-price rule: the line is backed by the lowest-priced
        // active in-stock vendor sku of whatever is being bought — the same
        // one the Vendor row names. Unreachable from a click (the button is
        // disabled on the same memo); kept for the type narrowing.
        if (!selectedVendorSku) return;
        setIsAdding(true);
        try {
            await addToCart({
                productId: product.id,
                quantity,
                vendorSkuId: selectedVendorSku.id,
                ...(selectedVariant ? { variantId: selectedVariant.id } : {}),
            });
        } catch (error) {
            // A coded refusal is shown inline (#443): stock — the API counts
            // what the cart already holds, and says how many MORE it can take
            // (`addable`) — or a product/offering gone, in the API's own
            // customer-safe words. useApi has toasted every other failure,
            // validation included. Either way, fall through WITHOUT navigating —
            // sending the shopper to a cart that never received the item would
            // read as success.
            // `addable` is always sent on an add; absent is read as none.
            const refusal = cartRefusal(error, ({ addable }) =>
                addableMessage(addable ?? 0),
            );
            if (refusal) {
                setAddRefusal({
                    vendorSkuId: selectedVendorSku.id,
                    quantity,
                    message: refusal.message,
                });
            }
            return;
        } finally {
            setIsAdding(false);
        }

        // Adding is a commitment step, so land the shopper on the cart rather
        // than leaving them on the product page with no confirmation.
        navigate(ROUTES.CART);
    };

    const notFoundView = (
        <div className="max-w-7xl mx-auto px-4 py-6 text-neutral">
            <Title>Product not found</Title>
        </div>
    );

    // Six outcomes, six cells. The order matters: a 404 is also a failed read,
    // so it is answered FIRST or it renders as a generic failure — and a
    // restricted product must reach its own cell rather than either.
    //
    //   1 loading, no product for this id         → Spinner
    //   2 failed, or settled with no product      → ErrorState + Retry
    //   3 failed with 404                         → not found
    //   4 200 with a restriction block            → not available (no price, no Add to Cart)
    //   5 200, no block, isAvailable false        → out of stock
    //   6 200, no block, available                → the full page
    //
    // "Not found" is an ANSWER — a 404 for this id — so it is never the fallback:
    // with no product and no settled failure the page is still loading. It is
    // the answer of the read whose status this is (PR 373 review 4): a new read
    // of the same id — a return to it on this mount, or a new destination —
    // starts at "loading", and the previous read's 404 does not stand in for it.
    // A failed re-read of a product already on screen (a ship-to change) clears
    // the product, so that failure lands in cell 2.

    if (
        readStatus === "failed" &&
        notFoundProductId !== null &&
        notFoundProductId === productId
    ) {
        return notFoundView;
    }

    if (!product && readStatus === "failed") {
        return (
            <div className="max-w-7xl mx-auto px-4 py-6">
                <ErrorState onRetry={reload} />
            </div>
        );
    }

    if (!product) {
        return (
            <div
                role="status"
                aria-label="Loading product"
                className="flex justify-center items-center min-h-[50vh]"
            >
                <Spinner size="lg" />
            </div>
        );
    }

    // Cell 4. Gated on the contract's own `isRestricted` rather than merely on
    // the block being present, so the flag the API sets is the flag the page
    // reads. Its own early return, NOT a branch inside the page below: every
    // price and the Add to Cart control have to be structurally absent, and a
    // conditional threaded through the live page is how one of them survives.
    if (product.restriction?.isRestricted) {
        return (
            <div className="bg-base-200 text-neutral">
                <div className="max-w-7xl mx-auto px-4 py-4 md:py-6 text-left">
                    <SubcategoryBreadcrumb chain={categoryChain} />

                    <ProductNotAvailable
                        product={product}
                        restriction={product.restriction}
                    />

                    {/* The same rail as the live page. It is read with the same
                        destination, so everything in it CAN ship there — which
                        makes it the third way out. */}
                    {/* Optional recommendation rail: hidden on a failed read by design (#223 ruling), not a failed state. */}
                    {relatedProducts.length > 0 && (
                        <ProductCarouselRow
                            title={`More in ${product.categoryName}`}
                            products={relatedProducts}
                            viewAllHref={ROUTES.BROWSE_CATEGORY(
                                product.categoryId,
                            )}
                        />
                    )}
                </div>
            </div>
        );
    }

    // The sub-label of the sku on screen: the selected variant's own, or the
    // base sku's when nothing is selected. A variant with no label of its own
    // shows no line — the base label is NOT inherited, because it names the
    // base sku's size/format and would misdescribe the variant being bought.
    // Falsy covers null, an absent field and "" alike, all of which mean the
    // line is suppressed.
    const displayLabel = selectedVariant
        ? selectedVariant.displayLabel
        : product.displayLabel;

    // Three states, not two. The row must name the vendor the cart line would
    // actually be billed to — `vendors[0]` was arbitrary on a multi-vendor
    // product and ignored the selected variant entirely, so a product supplied
    // only through a variant showed an em-dash beside a working Add to Cart.
    // The em-dash is now reserved for a sku with genuinely no supply rows;
    // rows that exist but are all inactive or out of stock get their own copy,
    // because naming a vendor there would promise a purchase the disabled
    // button cannot make, and an em-dash there would deny the product has a
    // supplier at all.
    // With no variant available there is no sku to name a vendor for (BR-04):
    // the product is unavailable, so the row says that — the em-dash stays
    // reserved for a sku that has no rows at all.
    const vendorName = selectedVendorSku
        ? selectedVendorSku.vendorName
        : selectedVendors.length > 0 || hasNoSkuToBuy
            ? "Currently unavailable"
            : "—";

    const shortDescription = truncateWords(
        product.description,
        SHORT_DESCRIPTION_WORDS,
    );

    const descriptionAccordion = (
        <InfoAccordion title="Description" defaultOpen>
            <p>{product.description}</p>
        </InfoAccordion>
    );

    return (
        <div className="bg-base-200 text-neutral">
            <div className="max-w-7xl mx-auto px-4 py-4 md:py-6 text-left">
                <SubcategoryBreadcrumb chain={categoryChain} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-2">
                    <div className="flex flex-col gap-6">
                        <ProductGallery product={product} />
                    </div>

                    <div className="flex flex-col gap-4">
                        <header className="space-y-1">
                            <h1 className="text-2xl md:text-3xl font-bold text-neutral">
                                {product.name} by {product.brand}
                            </h1>
                            {displayLabel && (
                                <p
                                    data-testid="sku-display-label"
                                    className="text-sm text-neutral/70"
                                >
                                    {displayLabel}
                                </p>
                            )}
                            {shortDescription && (
                                <p
                                    data-testid="short-description"
                                    className="md:hidden text-sm text-neutral/80"
                                >
                                    {shortDescription}
                                </p>
                            )}
                        </header>

                        {product.variants.length > 0 && (
                            <VariantChips
                                variants={product.variants}
                                selectedId={selectedVariantId ?? undefined}
                                onSelect={handleSelectVariant}
                            />
                        )}

                        {!isUnpriced && (
                            <div data-testid="product-pricing">
                                <p className="text-xl md:text-2xl font-bold text-neutral">
                                    <span>{pricing.unitPrice}</span>
                                    <span className="ml-2 text-base font-normal text-neutral/70">
                                        / unit
                                    </span>
                                    {pricing.showPiece && (
                                        <>
                                            <span className="mx-2 text-base font-normal text-neutral/40">
                                                /
                                            </span>
                                            <span>{pricing.piecePrice}</span>
                                            <span className="ml-2 text-base font-normal text-neutral/70">
                                                / piece
                                            </span>
                                        </>
                                    )}
                                </p>
                                {pricing.showPiece && (
                                    <p className="text-sm text-neutral/70">
                                        {pricing.piecesPerUnit} pieces per unit
                                    </p>
                                )}
                            </div>
                        )}

                        {/* An sku that can't be bought has nothing to count:
                            the stepper and the button are disabled, and both
                            point at the "Out of stock" line below. */}
                        <QuantityStepper
                            value={quantity}
                            onChange={setQuantity}
                            max={quantityCap(stockLimit)}
                            disabled={!isPurchasable}
                            describedBy={outOfStockReasonId}
                        />

                        {/* `!w-full` overrides the shared Button's own w-fit. */}
                        <Button
                            color={ComponentColor.Warning}
                            onClick={handleAddToCart}
                            disabled={!canAddToCart}
                            aria-describedby={outOfStockReasonId}
                            loading={isAdding}
                            className="!w-full text-neutral font-bold normal-case"
                        >
                            {isAdding ? "Adding…" : "Add to Cart"}
                        </Button>
                        {/* Keyed on whether the sku can be bought: a disabled
                            button with no reason strands the shopper, and
                            when the flag and the rows disagree the rows are
                            what the line would be backed by, so "Out of
                            stock" is what they say. A sku that can't be
                            bought says only that, whatever its rows count. A
                            quantity above the vendor's stock of a sku that
                            can be bought names the stock instead (#443). A
                            cart write in flight disables the button but is
                            not a reason to say "Out of stock". */}
                        {!isPurchasable ? (
                            <p
                                id={outOfStockId}
                                className="text-sm font-semibold text-error"
                            >
                                {OUT_OF_STOCK}
                            </p>
                        ) : (
                            isAboveStock && (
                                <p
                                    role="alert"
                                    className="text-sm font-semibold text-error"
                                >
                                    {stockNote}
                                </p>
                            )
                        )}
                        {/* At the stock: why the stepper's + stopped — unless
                            the page just lowered the quantity to it, and says
                            that instead. Not for a sku that can't be bought:
                            its stepper is disabled, and "Out of stock" says
                            why. */}
                        {isPurchasable &&
                            stockNote &&
                            !isAboveStock &&
                            !showQuantityLowered && (
                            <p className="text-sm text-neutral/70">
                                {stockNote}
                            </p>
                        )}
                        {showQuantityLowered && (
                            <p role="status" className="text-sm text-neutral/70">
                                {quantityLowered.message}
                            </p>
                        )}
                        {addRefusal &&
                            addRefusal.vendorSkuId === selectedVendorSku?.id &&
                            addRefusal.quantity === quantity && (
                            <p
                                role="alert"
                                className="text-sm font-semibold text-error"
                            >
                                {addRefusal.message}
                            </p>
                        )}

                        {/* A re-read took the chosen vendor away (review
                            finding 4): the vendor and price changed, so say why. */}
                        {vendorChoice.goneVendorName && (
                            <p role="status" className="text-sm text-neutral/70">
                                {selectedVendorSku
                                    ? `${vendorChoice.goneVendorName} is no longer available; showing ${selectedVendorSku.vendorName}.`
                                    : `${vendorChoice.goneVendorName} is no longer available.`}
                            </p>
                        )}

                        {/* An unavailable sku is bought from no one, so no
                            vendor is offered for it — even when its rows
                            disagree with that (#409). Held while a cart write
                            is out (review finding 2): the add in flight was
                            sent with the vendor on screen. */}
                        {inStock && (
                            <OtherVendors
                                offerings={offerings}
                                onChoose={handleChooseVendor}
                                disabled={isCartMutating}
                            />
                        )}

                        <div className="md:hidden">{descriptionAccordion}</div>

                        <InfoAccordion
                            title="Product Details"
                            defaultOpen
                            testId="product-details-section"
                        >
                            <dl className="grid grid-cols-[6rem_1fr] gap-y-2 gap-x-3">
                                <dt className="text-neutral/70">Brand:</dt>
                                <dd className="font-semibold">
                                    {product.brand}
                                </dd>
                                <dt className="text-neutral/70">Vendor:</dt>
                                <dd className="font-semibold">{vendorName}</dd>
                                <dt className="text-neutral/70">UPC:</dt>
                                <dd className="font-semibold">{product.upc}</dd>
                            </dl>
                            {/* I3: the product's COA downloads when it has one.
                                Without one the on-request note stays: the
                                ruling does not say what a product with no COA
                                shows. */}
                            {product.coa ? (
                                <ProductCoaDownload
                                    productId={product.id}
                                    coa={product.coa}
                                />
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setIsCoaModalOpen(true)}
                                    className="btn btn-link btn-sm px-0 text-info no-underline hover:underline"
                                >
                                    View Certificate
                                </button>
                            )}
                        </InfoAccordion>

                        <InfoAccordion title="Shipping Information">
                            {/* TODO(post-MVP): the shipping-information accordion was
                                deferred at E4 shaping (2026-06-05, "no MVP content");
                                /products carries no shipping payload. */}
                            <p className="text-neutral/70">
                                Shipping information is not yet available for
                                this product.
                            </p>
                        </InfoAccordion>
                    </div>
                </div>

                <div className="hidden md:block mt-6">{descriptionAccordion}</div>

                {/* Optional recommendation rail: hidden on a failed read by design (#223 ruling), not a failed state. */}
                {/* Titled for what it is (B-09): the product's own category read
                    back, not a relatedness-scored list. */}
                {relatedProducts.length > 0 && (
                    <ProductCarouselRow
                        title={`More in ${product.categoryName}`}
                        products={relatedProducts}
                        viewAllHref={ROUTES.BROWSE_CATEGORY(product.categoryId)}
                    />
                )}
            </div>

            {isCoaModalOpen && (
                <CoaModal onClose={() => setIsCoaModalOpen(false)} />
            )}
        </div>
    );
};
